import React, { useEffect, useMemo, useState } from 'react';
import { Col, Collapse, Row, Tab, Tabs } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useQueryState } from 'react-router-use-location-state';
import { ClipLoader } from 'react-spinners';
import hash from 'object-hash';
import { MultiValue } from 'react-select';
import { BalanceCollection } from '../../models/balance-collection-model';
import { Transactions } from '../../models/transactions-model';
import ApiService from '../../services/api-service';
import BlockchainAddressComponent from '../blockchain-address/blockchain-address-component';
import CollectionViewComponent from '../collection-view/collection-view-component';
import NoCollectiblesComponent from '../no-collectibles/no-collectibles-components';
import TransactionDetailsComponent from '../transaction-details/transaction-details-component';
import './wallet-component.scss';
import { User } from '../../models/user-model';
import UserAddresses from '../../models/user-addresses-model';
import Blockchain from '../../enums/blockchain-enum';
import HelpInfoComponent from '../help-info/help-info-component';
import MultiSelectComponent, { SelectOption } from '../multi-select/multi-select-component';
import Tools from '../../tools';
import NoCollectiblesType from '../../enums/no-collectibles-type-enum';

export default function WalletComponent(): JSX.Element {
  const api = new ApiService();

  type Params = {
    selectedAddress: string;
  };

  const [collection] = useQueryState('collection', '');
  const [collectionFilter, setCollectionFilter] = useState<string[]>(collection.length ? [collection] : []);
  const [blockchainFilter, setBlockchainFilter] = useState<string[]>([]);
  const [balance, setBalance] = useState<BalanceCollection[]>();
  const [filteredBalance, setFilteredBalance] = useState<BalanceCollection[]>();
  const { selectedAddress } = useParams<keyof Params>() as Params;
  const [sidebarVisible] = useState(true);
  const [transactions, setTransactions] = useState<Transactions>();
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [user, setUser] = useState<User>({} as User);
  const [userAddresses, setUserAddresses] = useState<UserAddresses>({
    counterparty: '',
    ethereum: '',
    firstOasisKlaytn: '',
  });

  const availableCollections = useMemo(() => {
    const sortedItems = (balance || []).map((x) => {
      return {
        value: x.id,
        label: x.name,
      };
    });
    sortedItems.sort((s1, s2) => (s1.value > s2.value ? 1 : -1));
    return sortedItems;
  }, [balance]);

  const availableBlockchains = useMemo(() => {
    return [
      { value: Blockchain.Counterparty, label: Tools.capitalizeFirstLetter(Blockchain.Counterparty) },
      { value: Blockchain.Ethereum, label: Tools.capitalizeFirstLetter(Blockchain.Ethereum) },
      { value: Blockchain.Klaytn, label: Tools.capitalizeFirstLetter(Blockchain.Klaytn) },
    ];
  }, [balance]);

  function filterByCollection(sourceCollection: BalanceCollection[], filter: string[]): BalanceCollection[] {
    return sourceCollection.filter((x) => filter.length === 0 || filter.includes(x.id));
  }

  function filterByBlockchain(sourceCollection: BalanceCollection[], filter: string[]): BalanceCollection[] {
    return sourceCollection.filter(
      (x) => filter.length === 0 || x.orbs?.map((o) => o.chain).filter((f) => filter.includes(f)).length > 0
    );
  }

  const onCollectionFilter = (values: MultiValue<SelectOption>) => {
    setCollectionFilter(values.map((x) => x.value));
  };

  const onBlockchainFilter = (values: MultiValue<SelectOption>) => {
    setBlockchainFilter(values.map((x) => x.value));
  };

  function fetchData(): void {
    api.getWalletBalanceAsync(selectedAddress).then((res) => {
      setBalance(res?.data?.collections);
      setFilteredBalance(res?.data?.collections);
    });

    api.getTransactionsAsync('', selectedAddress).then((res) => {
      setTransactions(res);
      setTransactionsLoading(false);
    });
  }

  function fetchUserAddresses(): void {
    api.getUserAddressesAsync(selectedAddress).then((userResponse: User) => {
      setUser(userResponse);

      const userResponseAddresses = { counterparty: '', ethereum: '', firstOasisKlaytn: '' };
      userResponse.data.addresses?.forEach((address) => {
        if (address.blockchain === Blockchain.Counterparty) {
          userResponseAddresses.counterparty = address.address;
        } else if (address.blockchain === Blockchain.Ethereum) {
          userResponseAddresses.ethereum = address.address;
        } else if (address.blockchain === Blockchain.FirstOasisKlaytn) {
          userResponseAddresses.firstOasisKlaytn = address.address;
        }
      });
      setUserAddresses(userResponseAddresses);
    });
  }

  useEffect(() => {
    fetchData();
    fetchUserAddresses();
  }, [selectedAddress, collection]);

  useEffect(() => {
    const filtered = filterByCollection(balance || [], collectionFilter);
    setFilteredBalance(filterByBlockchain(filtered, blockchainFilter));
  }, [collectionFilter, blockchainFilter]);

  return (
    <div id="page-wallet">
      <h1>Wallet</h1>
      <div>
        <Collapse in={sidebarVisible} dimension="width">
          <div id="wallet-block">
            <div className="mb-2">
              <BlockchainAddressComponent key={selectedAddress} user={user} />
            </div>
          </div>
        </Collapse>
        <div className="tab-content w-100 order-2 order-md-2">
          <div className="nav-tabs-responsive mb-3">
            <Tabs className="mb-3">
              <Tab
                eventKey="collections"
                title={
                  <span className="cursor-pointer">
                    Orbs
                    <HelpInfoComponent
                      infoTitle="Orbs"
                      infoText='ORB means "Ownership Read on the Blockchain".\n\nOrb is a combination of a token - containing blockchain information - and asset - containing off-chain information, also called metadata.'
                    />
                  </span>
                }
              >
                <div id="wallet-collection">
                  <Row>
                    <Col xs={12} md={6} className="mb-3">
                      <MultiSelectComponent
                        placeholder="Select Collection(s)…"
                        options={availableCollections}
                        onChange={onCollectionFilter}
                        values={collectionFilter}
                      />
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                      <MultiSelectComponent
                        placeholder="Select Blockchain(s)…"
                        options={availableBlockchains}
                        onChange={onBlockchainFilter}
                        values={blockchainFilter}
                      />
                    </Col>
                  </Row>

                  {filteredBalance && filteredBalance.length > 0 ? (
                    filteredBalance.map((x) => (
                      <CollectionViewComponent
                        item={x}
                        key={hash(x)}
                        selectedAddress={selectedAddress}
                        userAddresses={userAddresses}
                      />
                    ))
                  ) : (
                    <NoCollectiblesComponent
                      noCollectiblesType={
                        blockchainFilter.length > 0 || collectionFilter.length > 0
                          ? NoCollectiblesType.Filters
                          : NoCollectiblesType.Collectibles
                      }
                    />
                  )}
                </div>
              </Tab>
              <Tab eventKey="transactions" title={<span className="cursor-pointer">Transactions</span>}>
                <div>
                  {transactionsLoading ? (
                    <ClipLoader loading={transactionsLoading} />
                  ) : (
                    transactions && (
                      <div>
                        <TransactionDetailsComponent transactions={transactions} key={hash(transactions?.data)} />
                      </div>
                    )
                  )}
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
